/**
 * Global API client with automatic token refresh on 401 errors
 */
import AuthService from '../services/authService';
import SecureTokenStorage from './tokenStorage';
import logger from './logger';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface FetchOptions extends RequestInit {
  skipAuth?: boolean;
  retryCount?: number;
}

/**
 * Global fetch wrapper that automatically handles 401 errors by refreshing tokens
 */
export async function apiFetch(
  endpoint: string,
  options: FetchOptions = {}
): Promise<Response> {
  const { skipAuth = false, retryCount = 0, ...fetchOptions } = options;

  // Get auth headers
  const headersObj: Record<string, string> = {};

  // Copy existing headers
  if (fetchOptions.headers) {
    if (fetchOptions.headers instanceof Headers) {
      fetchOptions.headers.forEach((value, key) => {
        headersObj[key] = value;
      });
    } else if (Array.isArray(fetchOptions.headers)) {
      fetchOptions.headers.forEach(([key, value]) => {
        headersObj[key] = value;
      });
    } else {
      Object.assign(headersObj, fetchOptions.headers);
    }
  }

  if (!skipAuth) {
    const tokens = SecureTokenStorage.getTokens();
    if (tokens?.accessToken) {
      headersObj['Authorization'] = `Bearer ${tokens.accessToken}`;
    }
  }

  // Ensure Content-Type is set for non-FormData requests
  if (!headersObj['Content-Type'] && !(fetchOptions.body instanceof FormData)) {
    headersObj['Content-Type'] = 'application/json';
  }

  const headers: HeadersInit = headersObj;

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...fetchOptions,
    headers,
  });

  // Handle 401 errors with automatic token refresh
  if (response.status === 401 && !skipAuth && retryCount < 1) {
    logger.log('[apiFetch] Received 401, attempting token refresh...');

    try {
      await AuthService.refreshToken();
      logger.log('[apiFetch] Token refreshed, retrying request...');

      // Retry the request with the new token
      const newTokens = SecureTokenStorage.getTokens();
      if (newTokens?.accessToken) {
        const retryHeadersObj: Record<string, string> = { ...headersObj };
        retryHeadersObj['Authorization'] = `Bearer ${newTokens.accessToken}`;
        const retryHeaders: HeadersInit = retryHeadersObj;

        const retryResponse = await fetch(`${API_BASE_URL}${endpoint}`, {
          ...fetchOptions,
          headers: retryHeaders,
        });

        return retryResponse;
      } else {
        logger.error('[apiFetch] No access token after refresh');
        SecureTokenStorage.clearTokens();
      }
    } catch (refreshError) {
      logger.error('[apiFetch] Token refresh failed:', refreshError);
      // If refresh fails, clear tokens and return the original 401 response
      SecureTokenStorage.clearTokens();
    }
  }

  return response;
}

/**
 * Helper function to parse JSON response with error handling
 */
export async function apiFetchJson<T = any>(
  endpoint: string,
  options: FetchOptions = {}
): Promise<T> {
  const response = await apiFetch(endpoint, options);
  const data = await response.json();

  if (!response.ok) {
    const errorMsg =
      data.error ||
      data.message ||
      `Request failed with status ${response.status}`;
    throw new Error(errorMsg);
  }

  return data;
}

/**
 * Helper to get response data from API response format
 */
export function extractApiData<T>(response: {
  data?: T;
  [key: string]: unknown;
}): T {
  return (response.data || response) as T;
}
